import { createReducer, on } from '@ngrx/store';

import {
  activateFactor,
  exchangeToken,
  resendCodeToFactor,
  sendCodeToFactor,
  signIn,
  signInFailed,
  signInSuccess,
  updateTransaction,
  verifyFactor,
  onNewRegistration,
  cancelTransaction,
  enrollEmailFactor,
  enrollPhoneFactor,
  skipEnrollFactor,
  impersonateSuccess,
} from './login.actions';
import { SignInFailReason, AuthTransaction, LoginStatus } from './login.models';

export const LOGIN_FEATURE_KEY = 'login';

export interface LoginState {
  transaction?: AuthTransaction;
  hasTransaction: boolean;
  hasBeenSignedIn: boolean;
  signInError?: SignInFailReason;
  isTransactionInProgress: boolean;
  hasValidCredentials: boolean;
  canGoBack?: boolean;
  registrationPhoneNumber?: string;
  newPhoneNumber?: string;
  isANewUser?: boolean;
  factorVerification?: boolean;
  shouldSkipMfaWelcomeScreen?: boolean;
  isAfterRegistration: boolean;
}

export interface LoginPartialState {
  readonly [LOGIN_FEATURE_KEY]: LoginState;
}

export const initialState: LoginState = {
  hasBeenSignedIn: false,
  isTransactionInProgress: false,
  hasTransaction: false,
  hasValidCredentials: false,
  isAfterRegistration: false,
};

export const loginReducer = createReducer(
  initialState,
  on(updateTransaction, (state, { transaction }) => {
    if (!transaction.status) {
      return state;
    }
    return {
      ...state,
      transaction,
      isTransactionInProgress: false,
      hasTransaction: true,
      hasBeenSignedIn: false,
      signInError: undefined,
      hasValidCredentials:
        state.hasValidCredentials ||
        [
          LoginStatus.SUCCESS,
          LoginStatus.MFA_REQUIRED,
          LoginStatus.MFA_ENROLL,
        ].some((v) => v === transaction.status),
    };
  }),
  on(signInFailed, (state, { reason }) => ({
    ...state,
    signInError: reason,
    isTransactionInProgress: false,
  })),
  on(signInSuccess, (state) => ({
    ...initialState,
    isAfterRegistration: state.isAfterRegistration,
    hasBeenSignedIn: true,
    factorVerification: false,
    isANewUser: state.isANewUser,
  })),
  on(signIn, (state) => ({
    ...state,
    isTransactionInProgress: true,
  })),
  on(sendCodeToFactor, (state, { canGoBack }) => ({
    ...state,
    canGoBack,
    isTransactionInProgress: true,
  })),
  on(resendCodeToFactor, (state) => ({
    ...state,
    isTransactionInProgress: true,
  })),
  on(verifyFactor, (state) => ({
    ...state,
    isTransactionInProgress: true,
    factorVerification: true,
  })),
  on(exchangeToken, (state) => ({
    ...state,
    isTransactionInProgress: true,
  })),
  on(enrollEmailFactor, (state) => ({
    ...state,
    isTransactionInProgress: true,
    isANewUser: true,
  })),
  on(enrollPhoneFactor, (state, { phoneNumber }) => ({
    ...state,
    isTransactionInProgress: true,
    newPhoneNumber: phoneNumber,
  })),
  on(skipEnrollFactor, (state) => ({
    ...state,
    isTransactionInProgress: true,
  })),
  on(activateFactor, (state) => ({ ...state, isTransactionInProgress: true })),
  on(onNewRegistration, (state, { phoneNumber }) => ({
    ...state,
    registrationPhoneNumber: phoneNumber,
    shouldSkipMfaWelcomeScreen: true,
    isAfterRegistration: true,
  })),
  on(cancelTransaction, () => ({
    ...initialState,
  })),
  on(impersonateSuccess, (state) => ({ ...state, hasBeenSignedIn: true })),
);
